import React from "react";
import {logEvent, userEvents} from "../../utils/log";
import PropTypes from "prop-types";
import AppContext from "../../contexts/AppContext";
import i18n from "../../i18n";
import {assetUrl} from "../../utils/etc";
import processingManager from "../../photolab/ProcessingManager";
import Creative from "../../photolab/Creative";
import LazyImage from "../../components/LazyImage";
import ProcessingLoader from "../../components/ProcessingLoader/ProcessingLoader";
import Processing from "../../photolab/Processing";
import {FailedBtnStyled, FailedImageStyled, FailedStyled, FailedTextStyled, ItemStyled, OverlayStyled} from './CreativeView.style';
import {webviewAnalyticsEvent} from "../../utils/webview";
import clientStorage from "../../utils/client-storage";
import {contextTypeKeys, extraKeys} from "../../photolab/etc";

export default class CreativeView extends React.Component {

  state = {
    isImageLoaded: false,
    originalImageIsShown: false,
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.isSelected !== prevProps.isSelected) {
      this.logCreativeView();

      if (!this.props.isSelected && this.state.originalImageIsShown) {
        this.setState({originalImageIsShown: false});
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return nextProps.position !== this.props.position
      || nextProps.creative.id !== this.props.creative.id
      || nextState.isImageLoaded !== this.state.isImageLoaded
      || nextState.originalImageIsShown !== this.state.originalImageIsShown
      || nextProps.isSelected !== this.props.isSelected
      || (nextProps.isSelected && nextProps.creative.isFinished)
      ;
  }

  handleClick = (e) => {
    console.log(`${this.props.creative.group}/${this.props.creative.templateId}`);

    this.props.onClick && this.props.onClick();
  };

  startOriginalImageShowDelay = () => {
    clearTimeout(this.longPressTimer);
    this.longPressTimer = setTimeout(() => {
      this.setState({originalImageIsShown: true});
    }, 400);
  };

  handleTouchStart = (e) => {
    this.startOriginalImageShowDelay();
  };

  handleTouchMove = (e) => {
    if (!this.state.originalImageIsShown) {
      this.startOriginalImageShowDelay();
    }
  };

  handleTouchEnd = (e) => {
    if (e.touches && e.touches.length === 0) {
      clearTimeout(this.longPressTimer);
      this.setState({originalImageIsShown: false});
    }
  };

  handleRetryProcessingClick = () => {
    this.props.creative.markAsPending();
    this.props.creative.removeExtra(Creative.EXTRA_STARTED_AT);

    processingManager.retryCreative(this.props.creative);
    this.forceUpdate();
  };

  handleImageLoaded = (e) => {
    if (this.props.isOverlayed) {
      return;
    }

    this.setState({
      isImageLoaded: true
    }, this.logCreativeView);
  };

  logCreativeView = () => {
    if (!this.state.isImageLoaded || !this.props.isSelected) {
      return;
    }

    const processing = processingManager.processing;

    logEvent(userEvents.CREATIVE_VIEW, {
      group: this.props.creative.group,
      template_id: this.props.creative.templateId,
      position: this.props.position,
      image_url: this.props.creative.result,
      gender: this.props.creative.getExtra("gender"),
      section_name: processing.getExtra(extraKeys.sectionName),
      section_position: processing.getExtra(extraKeys.sectionPosition),
      block_name: processing.getExtra(extraKeys.blockName),
      block_position: processing.getExtra(extraKeys.blockPosition),
    });

    webviewAnalyticsEvent("template_shown", [
      clientStorage.getSelectedPhotosAmount(),
      this.props.position + 1,
      this.props.creative.templateId,
      this.props.creative.getExtra("body"),
      this.props.creative.getExtra("gender"),
      this.props.creative.getExtra("stylization"),
    ], {
      orig_bg: this.props.creative.getExtra(extraKeys.contextType) === contextTypeKeys.original ? "yes" : "no",
      block_cnt: clientStorage.getSelectedBlocksAmount(),
    });
  }

  handleImageFailedToLoad = (e) => {
    logEvent(userEvents.CREATIVE_IMAGE_LOAD_ERROR, {
      group: this.props.creative.group,
      template_id: this.props.creative.templateId,
      position: this.props.position,
      image_url: this.props.creative.result,
    });
  };

  render() {
    const classNames = [];
    let content = null;

    if (this.props.creative.isProcessed) {
      classNames.push("processed");
      this.state.originalImageIsShown && classNames.push("original-shown");

      content = <React.Fragment>
        <LazyImage
          className="original"
          src={this.props.originalImageUrl}
          alt=""
        />
        <LazyImage
          className="result"
          src={this.props.creative.result}
          alt=""
          onLoaded={this.handleImageLoaded}
          onFailedToLoad={this.handleImageFailedToLoad}
        />
        <OverlayStyled />
      </React.Fragment>;
    } else if (this.props.creative.isFailed) {
      classNames.push("failed");
      content = <FailedStyled>
        <FailedImageStyled
          src={assetUrl(`assets/images/icon-broken-heart.png`)}
          alt="" />
        <FailedTextStyled dangerouslySetInnerHTML={{__html: i18n.t("error_title")}} />
        <FailedBtnStyled onClick={this.handleRetryProcessingClick}>{i18n.t("retry")}</FailedBtnStyled>
      </FailedStyled>;
    } else if (this.props.creative.isPending) {
      let imagesUrls = processingManager.processing.getExtra(Processing.EXTRA_CROPPED_IMAGES_URLS);
      if (!imagesUrls) {
        imagesUrls = [processingManager.processing.getExtra(Processing.EXTRA_CROPPED_IMAGE_URL)];
      }

      content = <ProcessingLoader
        creative={this.props.creative}
        imagesUrls={imagesUrls}
        texts={[
          "processing_text_1",
          "processing_text_2",
          "processing_text_3",
          "processing_text_4",
        ]}
        stroke="rgb(5, 19, 32)"
        processingTookSecondsApprox={20}
      />;
    }

    return <ItemStyled
      className={classNames.join(" ")}
      onClick={this.handleClick}
      onTouchStart={this.handleTouchStart}
      onTouchMove={this.handleTouchMove}
      onTouchEnd={this.handleTouchEnd}
      onTouchCancel={this.handleTouchEnd}
      children={content}
    />
  }
}

CreativeView.contextType = AppContext;
CreativeView.propTypes = {
  creative: PropTypes.object.isRequired,
  position: PropTypes.number.isRequired,
};